<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';

// Props and emits
const props = defineProps<{ tournaments?: any[]; autoplayDelay?: number }>();
const emit = defineEmits<{
  (e: 'view', id: number): void;
}>();

const tournaments = computed(() => props.tournaments || []);

const featuredIndex = ref(0);
const isDragging = ref(false);
let dragStartX = 0;
let dragCurrentX = 0;
let isPointerDown = false;
const dragThreshold = 50;

const autoplayDelay = ref(props.autoplayDelay ?? 5000);
let autoplayTimer: ReturnType<typeof setInterval> | null = null;

const { isDesktop } = useLayout();

function stopAutoplay() {
  if (autoplayTimer) {
    clearInterval(autoplayTimer);
    autoplayTimer = null;
  }
}

function startAutoplay() {
  stopAutoplay();
  if (!import.meta.client) return;
  if (!isDesktop.value) return;
  if (tournaments.value.length <= 1) return;

  autoplayTimer = setInterval(() => {
    featuredIndex.value = (featuredIndex.value + 1) % tournaments.value.length;
  }, autoplayDelay.value);
}

function resetAutoplay() {
  stopAutoplay();
  startAutoplay();
}

function prevFeatured() {
  if (tournaments.value.length === 0) return;
  featuredIndex.value =
    (featuredIndex.value - 1 + tournaments.value.length) %
    tournaments.value.length;
}

function nextFeatured() {
  if (tournaments.value.length === 0) return;
  featuredIndex.value = (featuredIndex.value + 1) % tournaments.value.length;
}

function prevFeaturedNav() {
  prevFeatured();
  resetAutoplay();
}

function nextFeaturedNav() {
  nextFeatured();
  resetAutoplay();
}

function onPointerDown(e: PointerEvent) {
  if (!import.meta.client) return;
  try {
    if ('button' in e && (e as any).button !== 0) return;

    // ignore pointerdown events that start on interactive controls (buttons/links/inputs)
    try {
      const target = e.target as HTMLElement | null;

      if (target) {
        const interactive = target.closest(
          'button, a, input, textarea, select, label',
        );

        if (interactive) return;
      }
    } catch {}

    isPointerDown = true;
    isDragging.value = false;
    dragStartX = e.clientX;
    dragCurrentX = dragStartX;
    try {
      (e.currentTarget as HTMLElement)?.setPointerCapture?.(e.pointerId);
    } catch {}
    // avoid text selection while dragging
    try {
      e.preventDefault();
    } catch {}
  } catch {}
}

function onPointerMove(e: PointerEvent) {
  if (!isPointerDown) return;
  try {
    dragCurrentX = e.clientX;

    const delta = dragCurrentX - dragStartX;

    if (!isDragging.value && Math.abs(delta) > 10) {
      isDragging.value = true;
      stopAutoplay();
    }
  } catch {}
}

function onPointerUp(e: PointerEvent) {
  if (!isPointerDown) return;
  isPointerDown = false;
  try {
    (e.currentTarget as HTMLElement)?.releasePointerCapture?.(e.pointerId);
  } catch {}

  const delta = dragCurrentX - dragStartX;

  if (Math.abs(delta) > dragThreshold) {
    if (delta < 0) nextFeaturedNav();
    else prevFeaturedNav();
  }
  setTimeout(() => {
    isDragging.value = false;
    startAutoplay();
  }, 60);
}

const currentFeaturedTournament = computed(() => {
  return tournaments.value[featuredIndex.value] || null;
});

// Resolve a melhor propriedade de imagem do torneio (tenta várias chaves comuns)
const featuredImageSrc = computed(() => {
  const t: any = currentFeaturedTournament.value;

  if (!t) return '';

  const tryKeys = ['image1', 'image', 'cover', 'banner', 'img', 'image_url'];

  for (const k of tryKeys) {
    const val = t[k];

    if (!val) continue;

    // if value is string, assume it's the URL
    if (typeof val === 'string' && val.trim() !== '') return val;

    // if it's an object with url/src
    if (typeof val === 'object') {
      if (typeof val.url === 'string' && val.url.trim() !== '') return val.url;
      if (typeof val.src === 'string' && val.src.trim() !== '') return val.src;
      // common nested formats
      if (val[0] && typeof val[0] === 'string') return val[0];
    }
  }

  return '';
});

// Logs para ajudar a depurar por que a imagem não aparece
watch(
  currentFeaturedTournament,
  (t) => {
    try {
      console.debug('[TournamentFeatured] currentFeaturedTournament', t);
      console.debug(
        '[TournamentFeatured] resolved featuredImageSrc',
        featuredImageSrc.value,
      );
    } catch (e) {}
  },
  { immediate: true },
);

// transition control for featured banner (left/right/fade)
const previousFeaturedIndex = ref<number>(featuredIndex.value);
const featuredTransitionName = ref<string>('fade');

watch(featuredIndex, (newIndex, oldIndex) => {
  const len = Math.max(1, tournaments.value.length);

  if (oldIndex === undefined || oldIndex === null) {
    featuredTransitionName.value = 'fade';
  } else if ((oldIndex + 1) % len === newIndex) {
    featuredTransitionName.value = 'slide-left';
  } else if ((oldIndex - 1 + len) % len === newIndex) {
    featuredTransitionName.value = 'slide-right';
  } else {
    featuredTransitionName.value = 'fade';
  }

  previousFeaturedIndex.value = oldIndex ?? previousFeaturedIndex.value;
});

const featuredEndTime = computed(() => {
  const t = currentFeaturedTournament.value;

  if (!t) return Date.now();
  if (t.is_in_progress) return t.end_time;
  if (t.is_upcoming) return t.start_time;

  return Date.now();
});

const { remaining: featuredRemaining } = useTournamentTimer(featuredEndTime, {
  format: 'colon',
  autoStart: true,
  tickMs: 1000,
});

const featuredParts = computed(() => {
  const r = featuredRemaining.value || '00:00:00:00';
  const parts = r.split(':');

  if (parts.length === 4) return parts;

  return ['00', '00', '00', '00'];
});

watch(
  tournaments,
  (list) => {
    if (!list || list.length === 0) featuredIndex.value = 0;
    else if (featuredIndex.value >= list.length) featuredIndex.value = 0;
    startAutoplay();
  },
  { immediate: true },
);

onMounted(() => {
  startAutoplay();
  if (import.meta.client) {
    const handler = () => {
      try {
        if (document.hidden) stopAutoplay();
        else startAutoplay();
      } catch {}
    };

    document.addEventListener('visibilitychange', handler);
    (window as any).__tournamentFeaturedVisibilityHandler = handler;
  }
});

onUnmounted(() => {
  stopAutoplay();
  try {
    const handler = (window as any).__tournamentFeaturedVisibilityHandler;

    if (handler) document.removeEventListener('visibilitychange', handler);
  } catch {}
});

function handleView(id: number | undefined) {
  if (!id) return;
  emit('view', Number(id));
}
</script>

<template>
  <div
    v-if="isDesktop && tournaments.length > 0"
    class="mb-4"
    @mouseenter="stopAutoplay"
    @mouseleave="startAutoplay"
  >
    <div
      class="relative mb-4 rounded-lg overflow-hidden h-[320px] featured-draggable"
      :class="{ dragging: isDragging }"
      @pointercancel="onPointerUp"
      @pointerdown="onPointerDown"
      @pointerleave="onPointerUp"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
    >
      <transition
        mode="out-in"
        :name="featuredTransitionName"
      >
        <div
          v-if="currentFeaturedTournament"
          :key="featuredIndex"
          class="featuredSlide activeSlide"
        >
          <div
            v-if="currentFeaturedTournament.image1"
            class="relative w-full h-[280px] rounded-[20px] overflow-hidden cursor-pointer"
            @click="handleView(currentFeaturedTournament.instance_id)"
          >
            <NuxtImg
              alt="Torneio em Destaque"
              class="w-full h-full object-cover"
              draggable="false"
              :src="currentFeaturedTournament.image1"
            />
          </div>

          <div
            class="absolute left-1/2 -translate-x-1/2 bottom-[5px] flex justify-start text-white items-center gap-5 bg-bg-primary pl-5 pr-2.5 pt-2.5 pb-0 rounded-[10px_10px_0_0]"
          >
            <div class="min-w-[190px] select-none">
              <p
                class="text-[14px] font-bold mb-1.5 truncate overflow-ellipsis"
                >{{ currentFeaturedTournament.name }}</p
              >
              <div
                class="flex gap-1 text-[14px] font-bold justify-start items-center"
              >
                <p class="min-w-[90px]">Prêmio total</p>
                <p
                  class="text-[14px] font-bold text-[#00C9FF] truncate overflow-ellipsis"
                  >{{ currentFeaturedTournament.prize_pool_short }}</p
                >
              </div>
            </div>
            <div
              class="flex h-[67px] select-none justify-center items-center gap-[8px] [background:rgba(255,255,255,0.05)] p-2.5 rounded-[10px]"
            >
              <NuxtIcon
                class="text-[18px] text-white"
                name="tournament-single/clock"
              />
              <div class="flex items-center gap-2">
                <div class="text-center text-white">
                  <div class="text-[14px] font-extrabold">{{
                    featuredParts[0]
                  }}</div>
                  <div class="text-[10px] opacity-70">DIAS</div>
                </div>
                <div class="text-center text-white">
                  <div class="text-[14px] font-extrabold">{{
                    featuredParts[1]
                  }}</div>
                  <div class="text-[10px] opacity-70">HORAS</div>
                </div>
                <div class="text-center text-white">
                  <div class="text-[14px] font-extrabold">{{
                    featuredParts[2]
                  }}</div>
                  <div class="text-[10px] opacity-70">MINS</div>
                </div>
                <div class="text-center text-white">
                  <div class="text-[14px] font-extrabold">{{
                    featuredParts[3]
                  }}</div>
                  <div class="text-[10px] opacity-70">SEGS</div>
                </div>
              </div>

              <button
                v-if="currentFeaturedTournament.instance_id"
                class="flex h-[47px] min-w-[215px] justify-center items-center gap-[7px] border [background:var(--ATNB---ORANGE,radial-gradient(168.3%_138%_at_36.76%_-16.67%,#FF704A_0%,#FF3400_100%))] px-5 py-0 rounded-md border-solid border-[#FC9984]"
                @click.stop="handleView(currentFeaturedTournament.instance_id)"
              >
                <span
                  class="text-white text-[15px] font-extrabold leading-3 uppercase"
                  >participar agora</span
                >
                <NuxtIcon
                  class="text-[15px] text-white"
                  name="mission/button/participar"
                />
              </button>
            </div>
          </div>
        </div>
      </transition>

      <!-- Controls -->
      <button
        class="featuredNav"
        @click="prevFeaturedNav"
      >
        <NuxtIcon name="arrows/arrow_left" />
      </button>

      <button
        class="featuredNav rightNav"
        @click="nextFeaturedNav"
      >
        <NuxtIcon name="arrows/arrow_right" />
      </button>
    </div>
  </div>
</template>

<style lang="postcss">
/* Minimal necessary global styles for the featured banner (kept global to reuse existing selectors) */
.featuredSlide {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 320px;
  transition: opacity 0.4s ease;
  opacity: 0;
  pointer-events: none;
}
.activeSlide {
  opacity: 1;
  pointer-events: auto;
  z-index: 2;
}
.featuredNav {
  position: absolute !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  left: 12px;
  z-index: 30;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  box-sizing: border-box;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  font-size: 20px;
  line-height: 0;
  font-family: inherit;
  cursor: pointer;
  border: none;
}
.rightNav {
  left: auto;
  right: 12px;
}

:global(.featured-draggable) {
  cursor: grab;
  touch-action: pan-y pinch-zoom;
}
:global(.featured-draggable.dragging) {
  cursor: grabbing;
  user-select: none;
}

/* Transition classes (simple subset) */
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}
/* slide-left transition */
.slide-left-enter-from {
  transition:
    transform 0.9s cubic-bezier(0.2, 0.9, 0.2, 1),
    opacity 0.9s cubic-bezier(0.2, 0.9, 0.2, 1);
  opacity: 0;
}
.slide-left-enter-active {
  transition:
    transform 0.9s cubic-bezier(0.2, 0.9, 0.2, 1),
    opacity 0.9s cubic-bezier(0.2, 0.9, 0.2, 1);
}
.slide-left-leave-to {
  transform: translateX(-60%);
  opacity: 0;
}

/* slide-right transition */
.slide-right-enter-from {
  transform: translateX(-100%);
  opacity: 0;
}
.slide-right-enter-active {
  transition:
    transform 0.5s ease,
    opacity 0.5s ease;
}
.slide-right-enter-to {
  transform: translateX(0);
  opacity: 1;
}
.slide-right-leave-from {
  transform: translateX(0);
  opacity: 1;
}
.slide-right-leave-active {
  transition:
    transform 0.5s ease,
    opacity 0.5s ease;
}
.slide-right-leave-to {
  transform: translateX(60%);
  opacity: 0;
}
</style>
